// The eight symptom cards on /ask (mockup page 2).
//
// Six of them open a decision tree that already exists as a contract. The
// other two — "High Energy Bills" and "Something Else" — have no tree, so
// they open the free-text walk with the textarea pre-filled with a starter
// sentence the visitor can edit or delete. That's a client-side convenience,
// not a hidden answer: whatever ends up in the box is what gets sent.
import type { DecisionTree } from '@/shared/diagnostic/types'
import cooling from '@contracts/decision-tree.askhvac-cooling.v1.json'
import ice from '@contracts/decision-tree.askhvac-ice.v1.json'
import water from '@contracts/decision-tree.askhvac-water.v1.json'
import noise from '@contracts/decision-tree.askhvac-noise.v1.json'
import airflow from '@contracts/decision-tree.askhvac-airflow.v1.json'
import thermostat from '@contracts/decision-tree.askhvac-thermostat.v1.json'
import describe from '@contracts/decision-tree.askhvac-describe.v1.json'

export const DESCRIBE_TREE = describe as unknown as DecisionTree

export const TREES: Record<string, DecisionTree> = {
    'askhvac.cooling': cooling as unknown as DecisionTree,
    'askhvac.ice': ice as unknown as DecisionTree,
    'askhvac.water': water as unknown as DecisionTree,
    'askhvac.noise': noise as unknown as DecisionTree,
    'askhvac.airflow': airflow as unknown as DecisionTree,
    'askhvac.thermostat': thermostat as unknown as DecisionTree,
    'askhvac.describe': DESCRIBE_TREE,
}

export interface Symptom {
    id: string
    label: string
    sub: string
    /** Tree opened in the expert panel. */
    treeId: string
    /** Starter text for the free-text walk; ignored by the symptom trees. */
    seed?: string
    /** Inline SVG, 20x20 viewBox, currentColor. */
    icon: string
    /** Accent for the icon chip: blue by default, orange/green for variety
     *  matching the mockup's card row. */
    tone?: 'blue' | 'orange' | 'green'
}

const svg = (body: string) => body

export const SYMPTOMS: Symptom[] = [
    {
        id: 'not-cooling',
        label: 'Not Cooling',
        sub: 'Blowing warm air or not cooling',
        treeId: 'askhvac.cooling',
        tone: 'blue',
        // A thermometer, not a snowflake: "AC Freezing Up" below owns the
        // snowflake, and two of them side by side reads as the same card.
        icon: svg(
            '<path d="M11.6 11.1V4.2a1.6 1.6 0 1 0-3.2 0v6.9a3.4 3.4 0 1 0 3.2 0zm-1.6 5.6a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4z"/><path d="M14.4 5.4h3.1M14.4 8h2.2M14.4 2.8h2.2" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>',
        ),
    },
    {
        id: 'wont-start',
        label: "AC Won't Turn On",
        sub: 'No power or not starting',
        treeId: 'askhvac.thermostat',
        tone: 'orange',
        icon: svg(
            '<path d="M10 2.4v6.4" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/><path d="M6.1 5.2a5.6 5.6 0 1 0 7.8 0" stroke="currentColor" stroke-width="1.8" fill="none" stroke-linecap="round"/>',
        ),
    },
    {
        id: 'leaking',
        label: 'Leaking Water',
        sub: 'Water around the unit or inside',
        treeId: 'askhvac.water',
        tone: 'blue',
        icon: svg('<path d="M10 2.2c-2 3.6-5.4 5.6-5.4 9a5.4 5.4 0 0 0 10.8 0c0-3.4-3.4-5.4-5.4-9z"/>'),
    },
    {
        id: 'weak-airflow',
        label: 'Weak Airflow',
        sub: 'Low airflow or not enough air',
        treeId: 'askhvac.airflow',
        tone: 'blue',
        icon: svg(
            '<path d="M2.6 7h8.2a2.3 2.3 0 1 0-2.3-2.3M2.6 10.4h11.1a2.3 2.3 0 1 1-2.3 2.3M2.6 13.8h5.6" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>',
        ),
    },
    {
        id: 'high-bills',
        label: 'High Energy Bills',
        sub: 'Bills higher than normal',
        treeId: 'askhvac.describe',
        seed: 'My electric bill has gone up and the AC seems to run more than it used to. ',
        tone: 'green',
        icon: svg(
            '<path d="M10.9 2v1.4c1.7.2 2.9 1.1 3.1 2.7h-2.2c-.1-.6-.6-1-1.5-1-1 0-1.6.4-1.6 1s.4.9 1.9 1.2c2.4.5 3.5 1.3 3.5 3.1 0 1.7-1.2 2.8-3.2 3V15H9.2v-1.5c-2-.2-3.3-1.3-3.4-3h2.2c.1.8.8 1.3 1.9 1.3 1.1 0 1.7-.4 1.7-1.1 0-.6-.4-.9-2-1.3-2.3-.5-3.3-1.3-3.3-3C6.3 4.8 7.4 3.7 9.2 3.5V2z"/>',
        ),
    },
    {
        id: 'freezing',
        label: 'AC Freezing Up',
        sub: 'Ice on the coils or lines',
        treeId: 'askhvac.ice',
        tone: 'blue',
        icon: svg(
            '<path d="M10 2.4v15.2M4.4 5.6l11.2 6.8M15.6 5.6 4.4 12.4" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/><path d="M10 6.2 8.3 4.6M10 6.2l1.7-1.6M10 13.8l-1.7 1.6M10 13.8l1.7 1.6" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/>',
        ),
    },
    {
        id: 'noise',
        label: 'Making Noise',
        sub: 'Squealing, grinding, banging',
        treeId: 'askhvac.noise',
        tone: 'orange',
        icon: svg(
            '<path d="M4 8h2.6L10 4.8v10.4L6.6 12H4z"/><path d="M12.6 7.4a3.6 3.6 0 0 1 0 5.2M14.8 5.2a6.8 6.8 0 0 1 0 9.6" stroke="currentColor" stroke-width="1.4" fill="none" stroke-linecap="round"/>',
        ),
    },
    {
        id: 'something-else',
        label: 'Something Else',
        sub: 'Describe a different issue',
        treeId: 'askhvac.describe',
        tone: 'blue',
        icon: svg(
            '<path d="M10 2a8 8 0 1 0 0 16 8 8 0 0 0 0-16zm.9 12.4H9.1v-1.8h1.8zm1.3-5.1c-.3.4-.7.8-1.2 1.1-.3.2-.5.4-.6.6-.1.2-.1.4-.1.7H9.1c0-.6.1-1 .3-1.3.2-.3.5-.6 1-.9.3-.2.5-.4.6-.6.1-.2.2-.4.2-.7 0-.3-.1-.5-.3-.7a1 1 0 0 0-.8-.3c-.3 0-.6.1-.8.3-.2.2-.3.5-.3.8H7.4c0-.8.3-1.4.8-1.8.5-.4 1.1-.6 1.9-.6.8 0 1.5.2 2 .6.5.4.7 1 .7 1.7 0 .4-.1.8-.3 1.1z"/>',
        ),
    },
]

/** The four "Popular AC Problems" cards at the foot of the page. Each one
 *  opens the tree that actually answers it, rather than linking to an
 *  article that hasn't been written yet (M11). */
export const POPULAR = [
    {
        title: 'Why is my AC running but not cooling?',
        blurb: 'The most common call of the summer. Six questions separate the filter from the compressor.',
        treeId: 'askhvac.cooling',
    },
    {
        title: 'Why is my AC freezing up?',
        blurb: 'Ice means starved airflow or low refrigerant — and one of those is free to fix.',
        treeId: 'askhvac.ice',
    },
    {
        title: 'Why is my AC leaking water?',
        blurb: 'Usually a blocked drain line. Sometimes it is not, and that part matters.',
        treeId: 'askhvac.water',
    },
    {
        title: 'Why is my AC making that noise?',
        blurb: 'Squeal, grind, bang or buzz — the sound narrows it down more than you would think.',
        treeId: 'askhvac.noise',
    },
]
